// hooks/useQuickScoringAutoFocus.ts
import { useRef } from 'react'
import { useContext } from 'react'
import { useFormContext } from 'react-hook-form'
import { TentativeScoringQuestionContext } from '@app.feature/exam/gsat2024-2/context/TentativeScoringQuestionContext'
import { TentativeScoringQuestionListFormType } from '@app.feature/exam/gsat2024-2/types/tentativeScoringQuestionListForm.type'
import createQuestionGroups, {
  TQuestion,
} from '@app.feature/exam/modules/createQuestionGroups'

type SubjectType = 'math' | 'reasoning'

type TUseQuickScoringAutoFocusReturns = {
  mathQuestionGroups: Array<Array<TQuestion>>
  reasoningQuestionGroups: Array<Array<TQuestion>>
  registerInput: (
    subject: SubjectType,
    seq: number,
    element: HTMLInputElement | null,
  ) => void
  focusNextEmptyInput: (subject: SubjectType, currentSeq: number) => void
}

/**
 * 빠른 채점 페이지의 자동 포커스 이동 기능을 제공하는 커스텀 훅
 *
 * 이 훅은 수리 영역과 추리 영역의 문제 그룹을 관리하고,
 * 입력 필드 간 자동 포커스 이동 기능을 제공합니다.
 *
 * 사용자가 한 필드에 값을 입력하면 자동으로 다음 빈 입력 필드로 포커스가 이동합니다.
 * 수리 영역의 마지막 문제를 입력하면 추리 영역의 첫 번째 빈 필드로 포커스가 이동합니다.
 *
 * @returns {TUseQuickScoringAutoFocusReturns} 문제 그룹과 포커스 관련 함수들을 포함하는 객체
 * @returns {Array<Array<TQuestion>>} mathQuestionGroups - 수리 영역 문제 그룹
 * @returns {Array<Array<TQuestion>>} reasoningQuestionGroups - 추리 영역 문제 그룹
 * @returns {Function} registerInput - 입력 필드 참조를 등록하는 함수
 * @returns {Function} focusNextEmptyInput - 다음 빈 입력 필드로 포커스를 이동하는 함수
 */
const useQuickScoringAutoFocus = (): TUseQuickScoringAutoFocusReturns => {
  const { examGradingQuestionData } = useContext(
    TentativeScoringQuestionContext,
  )
  const { watch } = useFormContext<TentativeScoringQuestionListFormType>()

  const mathQuestionGroups =
    createQuestionGroups(examGradingQuestionData?.math) || []

  const reasoningQuestionGroups =
    createQuestionGroups(examGradingQuestionData?.reasoning) || []

  const allMathQuestions = mathQuestionGroups.flat()
  const allReasoningQuestions = reasoningQuestionGroups.flat()

  const inputRefs = useRef({
    math: new Map<number, HTMLInputElement>(),
    reasoning: new Map<number, HTMLInputElement>(),
  })

  const registerInput = (
    subject: SubjectType,
    seq: number,
    element: HTMLInputElement | null,
  ) => {
    if (element) {
      inputRefs.current[subject].set(seq, element)
    } else {
      inputRefs.current[subject].delete(seq)
    }
  }

  const findNextEmptyInput = (subject: SubjectType, currentSeq: number) => {
    const questions =
      subject === 'math' ? allMathQuestions : allReasoningQuestions
    const refs = inputRefs.current[subject]

    const currentIndex = questions.findIndex((q) => q.seq === currentSeq)
    if (currentIndex === -1) return null

    for (let i = currentIndex + 1; i < questions.length; i++) {
      const question = questions[i]
      const input = refs.get(question.seq)
      const value = watch(
        `${subject}.inputQuestions.${question.seq - 1}.selectedAnswer`,
      )

      if (!value && input) {
        return input
      }
    }

    if (subject === 'math') {
      for (const question of allReasoningQuestions) {
        const input = inputRefs.current.reasoning.get(question.seq)
        const value = watch(
          `reasoning.inputQuestions.${question.seq - 1}.selectedAnswer`,
        )

        if (!value && input) {
          return input
        }
      }
    }
    return null
  }

  const focusNextEmptyInput = (subject: SubjectType, currentSeq: number) => {
    const nextInput = findNextEmptyInput(subject, currentSeq)
    if (nextInput) {
      nextInput.focus()
    }
  }

  return {
    mathQuestionGroups,
    reasoningQuestionGroups,
    registerInput,
    focusNextEmptyInput,
  }
}

export default useQuickScoringAutoFocus
